import ImportedSourceData from '../components/ImportedSourceData';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Domain as DomainIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { domainService } from '../services/api';
import Layout from '../components/Layout/Layout';

const DomainList = () => {
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState('cards');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadDomains();
  }, []);

  useEffect(() => {
    filterDomains();
  }, [domains, searchTerm, statusFilter, typeFilter, activeFilter]);

  const loadDomains = async () => {
    try {
      const response = await domainService.getDomains();
      setDomains(response.domains || []);
    } catch (error) {
      console.error('Erro ao carregar domínios:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar domínios',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const filterDomains = () => {
    let filtered = domains;

    // Filtro por termo de busca
    if (searchTerm) {
      filtered = filtered.filter(domain =>
        domain.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        domain.domain_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        domain.domain_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(domain => domain.status === statusFilter);
    }

    // Filtro por tipo
    if (typeFilter !== 'all') {
      filtered = filtered.filter(domain => domain.domain_type === typeFilter);
    }

    // Filtro por ativo/inativo
    if (activeFilter !== 'all') {
      filtered = filtered.filter(domain => domain.is_active === (activeFilter === 'true'));
    }

    setFilteredDomains(filtered);
  };

  const handleEdit = (domainId) => {
    navigate(`/domains/${domainId}/edit`);
  };

  const handleDelete = async (domainId) => {
    if (window.confirm('Tem certeza que deseja excluir este domínio?')) {
      try {
        await domainService.deleteDomain(domainId);
        setSnackbar({
          open: true,
          message: 'Domínio excluído com sucesso',
          severity: 'success'
        });
        loadDomains();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message || 'Erro ao excluir domínio',
          severity: 'error'
        });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'expired': return 'error';
      case 'pending': return 'warning';
      case 'maintenance': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return '🟢 Ativo';
      case 'inactive': return '🔴 Inativo';
      case 'expired': return '⏰ Expirado';
      case 'pending': return '⏳ Pendente';
      case 'maintenance': return '🟡 Manutenção';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'expired': return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'pending': return <WarningIcon sx={{ color: 'warning.main' }} />;
      case 'maintenance': return <WarningIcon sx={{ color: 'warning.main' }} />;
      default: return <DomainIcon />;
    }
  };

  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const today = new Date();
    const expiration = new Date(expirationDate);
    const diffTime = expiration - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
          <Typography variant="h4" gutterBottom>
            Carregando...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
              <DomainIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">
                Gerenciamento de Domínios
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredDomains.length} domínios encontrados
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/domains/new')}
          >
            Novo Domínio
          </Button>
        </Box>

        {/* Filtros e Busca */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Buscar domínios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Ativo</MenuItem>
                  <MenuItem value="inactive">Inativo</MenuItem>
                  <MenuItem value="expired">Expirado</MenuItem>
                  <MenuItem value="pending">Pendente</MenuItem>
                  <MenuItem value="maintenance">Manutenção</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Tipo"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value=".com">.com</MenuItem>
                  <MenuItem value=".com.br">.com.br</MenuItem>
                  <MenuItem value=".org">.org</MenuItem>
                  <MenuItem value=".net">.net</MenuItem>
                  <MenuItem value="Internacional">Internacional</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="true">Ativo</MenuItem>
                  <MenuItem value="false">Inativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('cards')}
                >
                  Cards
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('table')}
                >
                  Tabela
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista de Domínios */}
        {viewMode === 'cards' ? (
          <Grid container spacing={3}>
            {filteredDomains.map((domain) => (
              <Grid item xs={12} sm={6} md={4} key={domain.id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                        {getStatusIcon(domain.status)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {domain.name}
                        </Typography>
                          <ImportedSourceData product={domain} />
                        <Chip
                          label={getStatusLabel(domain.status)}
                          color={getStatusColor(domain.status)}
                          size="small"
                        />
                      </Box>
                    </Box>

                    {domain.domain_name && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Domínio:</strong> {domain.domain_name}{domain.domain_type && domain.domain_type}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {domain.domain_type && (
                        <Typography variant="body2">
                          <strong>Tipo:</strong> {domain.domain_type}
                        </Typography>
                      )}

                      {domain.registration_date && (
                        <Typography variant="body2">
                          <CalendarIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          Registrado: {new Date(domain.registration_date).toLocaleDateString('pt-BR')}
                        </Typography>
                      )}

                      {domain.expiration_date && (
                        <Typography variant="body2" sx={{
                          color: isExpiringSoon(domain.expiration_date) ? 'warning.main' : 'inherit'
                        }}>
                          <CalendarIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          Expira: {new Date(domain.expiration_date).toLocaleDateString('pt-BR')}
                          {isExpiringSoon(domain.expiration_date) && (
                            <Chip label="Expira em breve" size="small" color="warning" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                      )}

                      {domain.renewal_value !== null && domain.renewal_value !== undefined && (
                        <Typography variant="body2">
                          <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          Valor de renovação: R$ {Number(domain.renewal_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </Typography>
                      )}

                      {domain.location && (
                        <Typography variant="body2">
                          <LocationIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          {domain.location}
                        </Typography>
                      )}

                      {domain.technician_in_charge && (
                        <Typography variant="body2">
                          <PersonIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          {domain.technician_in_charge}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>

                  {/* Botões de ação */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(domain.id)}
                      sx={{ mr: 1 }}
                    >
                      Editar
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(domain.id)}
                    >
                      Excluir
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Domínio</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expiração</TableCell>
                  <TableCell>Valor Renovação</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDomains.map((domain) => (
                  <TableRow key={domain.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{domain.name}</Typography>
                        <ImportedSourceData product={domain} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {domain.domain_name ? (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {domain.domain_name}{domain.domain_type && domain.domain_type}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{domain.domain_type || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(domain.status)}
                        color={getStatusColor(domain.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {domain.expiration_date ? (
                        <Typography variant="body2" sx={{
                          color: isExpiringSoon(domain.expiration_date) ? 'warning.main' : 'inherit'
                        }}>
                          {new Date(domain.expiration_date).toLocaleDateString('pt-BR')}
                          {isExpiringSoon(domain.expiration_date) && (
                            <Chip label="Expira em breve" size="small" color="warning" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {domain.renewal_value !== null && domain.renewal_value !== undefined
                        ? `R$ ${Number(domain.renewal_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(domain.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(domain.id)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(domain.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filteredDomains.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Nenhum domínio encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || activeFilter !== 'all'
                ? 'Tente ajustar os filtros de busca'
                : 'Comece cadastrando seu primeiro domínio'
              }
            </Typography>
          </Paper>
        )}

        {/* Snackbar para notificações */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default DomainList;
